import { Component, inject, OnDestroy, signal } from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";

import { IconButtonComponent } from "@portal/shared/components/icon-button/icon-button.component";
import { SafeHtmlPipe } from "@portal/shared/pipes/safe-html.pipe";
import { FileUtils } from "@ramudden/core/utils";
import {
    AccessibilityService,
    DocumentEventService,
    DownloadFileService,
    ImageConverterService,
} from "@ramudden/services";
import { SpinnerComponent } from "@ramudden/ui";

export enum GalleriaPreviewer {
    Photo,
    Pdf,
    Video,
}

export interface IGalleriaImage {
    source: string;
    previewImageSrc: string;
    title: string;
    alt: string;
    previewer: GalleriaPreviewer;
}

export type GalleriaPdfState = "loading" | "ready" | "failed";

@Component({
    selector: "app-galleria",
    templateUrl: "./galleria.component.html",
    imports: [IconButtonComponent, SafeHtmlPipe, SpinnerComponent, TranslateModule],
})
export class GalleriaComponent implements OnDestroy {
    private _visible = false;
    get visible(): boolean {
        return this._visible;
    }

    private _images: IGalleriaImage[];
    get images(): IGalleriaImage[] {
        return this._images;
    }

    private _index = 0;
    get index(): number {
        return this._index;
    }

    set index(index: number) {
        this._index = index;
    }

    get currentImage(): IGalleriaImage {
        return this._images[this.index];
    }

    private _navigatorImages: IGalleriaImage[];
    get navigatorImages(): IGalleriaImage[] {
        return this._navigatorImages;
    }

    loop = true;
    navigator = true;
    navigatorVisibleImages = 5;

    // Mobile browsers can't show a PDF inside a page (iOS claims it can but renders only the first page as an image),
    // so touch devices get an "Open PDF" button instead of the iframe.
    readonly showPdfInline = navigator.pdfViewerEnabled && !window.matchMedia("(pointer: coarse)").matches;
    readonly pdfUrl = signal<string | undefined>(undefined);
    readonly pdfState = signal<GalleriaPdfState>("loading");
    private pdfRequestId = 0;

    private readonly accessibilityService = inject(AccessibilityService);
    private readonly documentEventService = inject(DocumentEventService);
    private readonly downloadFileService = inject(DownloadFileService);
    private readonly imageConverterService = inject(ImageConverterService);

    ngOnDestroy() {
        this.releasePdf();
    }

    show(images: IGalleriaImage[], indexStart = 0) {
        if (!images || !images.length) {
            this.close();
            return;
        }

        this._images = images;
        this.index = indexStart;

        for (const image of this.images) {
            image.previewer = this.GetPreviewer(image);

            if (!image.previewImageSrc || image.source === image.previewImageSrc) {
                image.previewImageSrc =
                    image.previewer === GalleriaPreviewer.Pdf
                        ? "/assets/img/pdf-preview.png"
                        : image.previewer === GalleriaPreviewer.Video
                          ? "/assets/img/video-preview.png"
                          : image.previewImageSrc;
            }
        }

        this.onCurrentImageChanged();
        this.bindKeys();

        this._visible = true;
    }

    GetPreviewer(image: IGalleriaImage): GalleriaPreviewer {
        return FileUtils.isPdfUrl(image.source)
            ? GalleriaPreviewer.Pdf
            : FileUtils.isVideoUrl(image.source)
              ? GalleriaPreviewer.Video
              : GalleriaPreviewer.Photo;
    }

    private bindKeys() {
        this.documentEventService.addOnKeyPress("Escape", () => {
            this.close();
        });

        this.documentEventService.addOnKeyPress(
            "ArrowLeft",
            () => {
                this.previous();
            },
            true,
        );

        this.documentEventService.addOnKeyPress(
            "ArrowRight",
            () => {
                this.next();
            },
            true,
        );
    }

    private unbindKeys() {
        this.documentEventService.removeOnKeyPress("Escape");
        this.documentEventService.removeOnKeyPress("ArrowLeft");
        this.documentEventService.removeOnKeyPress("ArrowRight");
    }

    get hasPrevious(): boolean {
        return this.images && this.images.length > 1 && (this.loop || this.index - 1 > 0);
    }

    get hasNext(): boolean {
        return this.images && this.images.length > 1 && (this.loop || this.index + 1 < this.images.length);
    }

    previous() {
        if (--this.index < 0) this.index = this.images.length - 1;
        this.onCurrentImageChanged();
    }

    next() {
        if (++this.index >= this.images.length) this.index = 0;
        this.onCurrentImageChanged();
    }

    navigateToImage(image: IGalleriaImage) {
        this.index = this.images.indexOf(image);
        this.onCurrentImageChanged();
    }

    openPdf() {
        window.open(this.pdfUrl(), "_blank");
    }

    private onCurrentImageChanged() {
        this.refreshNavigatorImages();
        this.loadCurrentPdf();
    }

    // Attachments are stored as application/octet-stream, so an iframe on the storage URL would download the file.
    // Fetching it and re-typing it as a PDF blob makes the browser show it instead.
    private loadCurrentPdf() {
        this.releasePdf();
        if (this.currentImage?.previewer !== GalleriaPreviewer.Pdf) return;

        const requestId = this.pdfRequestId;
        this.pdfState.set("loading");
        this.downloadFileService.downloadBlob(this.currentImage.source).then(
            (downloadedFile) => {
                const url = URL.createObjectURL(new Blob([downloadedFile.blob], { type: "application/pdf" }));
                if (requestId !== this.pdfRequestId) {
                    URL.revokeObjectURL(url);
                    return;
                }

                this.pdfUrl.set(url);
                this.pdfState.set("ready");
            },
            () => {
                if (requestId === this.pdfRequestId) this.pdfState.set("failed");
            },
        );
    }

    // Frees the current PDF's memory and invalidates any download still running for it.
    private releasePdf() {
        this.pdfRequestId++;
        const url = this.pdfUrl();
        if (!url) return;

        URL.revokeObjectURL(url);
        this.pdfUrl.set(undefined);
    }

    private refreshNavigatorImages() {
        if (!this.images) return;

        const indexMargin = (this.navigatorVisibleImages - 1) / 2;
        let beginImageIndex = this.index - indexMargin;
        let endImageIndex = this.index + indexMargin;

        // If less images than we wanna show
        // Or not looping and going before 0
        // Set start index to 0
        if (this.images.length < this.navigatorVisibleImages || (beginImageIndex < 0 && !this.loop)) {
            beginImageIndex = 0;
            endImageIndex = Math.min(this.images.length - 1, this.navigatorVisibleImages);
        }

        const images = new Array<IGalleriaImage>();

        if (beginImageIndex === endImageIndex) {
            images.push(this.currentImage);
        } else {
            for (let index = beginImageIndex; index <= endImageIndex; index++) {
                let actualIndex = index;
                if (actualIndex < 0) actualIndex = this.images.length + index;
                if (actualIndex > this.images.length - 1) actualIndex = index - this.images.length;

                images.push(this.images[actualIndex]);
            }
        }

        this._navigatorImages = images;
    }

    download() {
        this.downloadFileService.downloadBlob(this.currentImage.source).then(async (downloadedFile) => {
            // If current image is webp, convert to jpg
            if (downloadedFile.file.type === "image/webp") {
                await this.imageConverterService.convertAndDownloadWebpToJpeg(
                    await FileUtils.toBase64(downloadedFile.file),
                    downloadedFile.file.name,
                );
            } else {
                downloadedFile.save();
            }
        });
    }

    copyToClipboard() {
        this.accessibilityService.copyToClipboard(this.currentImage.source);
    }

    close() {
        this._visible = false;
        this.unbindKeys();
        this.releasePdf();
    }
}
